import React, {
  ReactNode,
  useContext,
  useEffect,
  useMemo,
  useState
} from "react";
import { Box, Link, Step, StepLabel, Stepper, Typography } from "@mui/material";
import { motion } from "framer-motion";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { useNavigate } from "react-router-dom";
import {
  BRAND_COLOR,
  KivuI18nContext,
  KivuEnvConfigContext,
  localizedPath,
  useKivunovaTranslation,
  usePlatformConfig,
  PhoneCountryCodeMap,
  API_RESPONSE_CODE,
  copyrightText,
  PAGE_ROUTE_LOGIN
} from "@kivunova/kivufrontendcommon";
import { getPrivacyPolicyUrl } from "../../Utils/kivunovaWebsite";
import { RegistrationStepConfig } from "./constants/registrationConstants";
import { RegistrationData } from "./types/registrationTypes";
import AdminInfoForm from "./AdminInfoForm";
import CompanyInfoForm from "./CompanyInfoForm";
import ReviewAndSubmit from "./ReviewAndSubmit";
import { useAppDispatch, useAppSelector } from "../../hooks/reduxHooks";
import { setFormData } from "../../store/slices/registrationSlice";
import AuthSidePanel from "../common/AuthSidePanel/AuthSidePanel";
import { linkButtonSx } from "../common/constants/buttonStyles";
import PageActionBar from "../common/responsive/PageActionBar";
import RenderRegistrationIcon from "./RenderRegistrationIcon";
import AppButton from "../common/AppButton";
import {
  authFormCardMotion,
  authFormItemMotion
} from "../common/constants/authFormStyles";
import { defaultRegistrationValues } from "./constants/registrationConstants";
import {
  getRegistrationAdminSchema,
  getRegistrationCompanySchema,
  transformRegistrationToRequest
} from "./utils/registrationUtils";
import {
  getRegistrationBusinessTypeValues,
  getRegistrationPlatformConfigState
} from "./utils/registrationBusinessTypeOptions";
import { getOrganizationLabelsForBusinessType } from "../../hooks/useOrganizationLabels";
import useCreateAccount from "../../hooks/api/useCreateAccount";
import AuthRightSidePanel from "../common/AuthSidePanel/AuthRightSidePanel";

import { LOG_LEVEL, buildLogContext, sendAppLog } from "../../Utils/appLogger";

const Registration: React.FC = () => {
  const dispatch = useAppDispatch();
  const useCreateAccountMutation = useCreateAccount();
  const originalFormData = useAppSelector(
    (state) =>
      state.registration?.registrationFormData ?? defaultRegistrationValues
  );
  const { t } = useKivunovaTranslation();
  const navigate = useNavigate();
  const { language, defaultLang } = useContext(KivuI18nContext) || {};
  const { countryCode: envCountryCode } =
    useContext(KivuEnvConfigContext) || {};
  const { businessTypes, loadError: platformConfigLoadError } =
    usePlatformConfig();
  const { registryLoaded, isPlatformConfigLoading } = useMemo(
    () =>
      getRegistrationPlatformConfigState(
        businessTypes,
        platformConfigLoadError
      ),
    [businessTypes, platformConfigLoadError]
  );
  const allowedRegistrationBusinessTypes = useMemo(
    () => getRegistrationBusinessTypeValues(businessTypes, registryLoaded),
    [businessTypes, registryLoaded]
  );
  const [activeStep, setActiveStep] = useState(0);
  const isCompanyStepBlockedByPlatformConfig =
    activeStep === 1 && isPlatformConfigLoading;
  const cloneDeep = <T,>(obj: T): T =>
    JSON.parse(JSON.stringify(obj != null ? obj : {}));

  const phoneMapKeys = Object.keys(PhoneCountryCodeMap || {});
  const defaultPhoneCountryCode =
    (envCountryCode && PhoneCountryCodeMap?.[envCountryCode]?.countryCode) ||
    (phoneMapKeys.length > 0
      ? PhoneCountryCodeMap[phoneMapKeys[0]]?.countryCode
      : null) ||
    "";

  const formData = {
    admin: {
      ...cloneDeep(originalFormData?.admin ?? defaultRegistrationValues.admin),
      phoneCountryCode:
        originalFormData?.admin?.phoneCountryCode || defaultPhoneCountryCode
    },
    company: cloneDeep(
      originalFormData?.company ?? defaultRegistrationValues.company
    )
  };

  const adminSchema = useMemo(() => {
    return getRegistrationAdminSchema(t);
  }, []);

  const companySchema = useMemo(() => {
    const labels = getOrganizationLabelsForBusinessType(
      t,
      formData.company.businessType
    );
    return getRegistrationCompanySchema(
      t,
      allowedRegistrationBusinessTypes,
      labels.nameRequiredError
    );
  }, [t, allowedRegistrationBusinessTypes, formData.company.businessType]);

  const schemaList = [adminSchema, companySchema];
  const currentSchema = schemaList[activeStep] || adminSchema;
  const currentStepConfig = RegistrationStepConfig[activeStep];

  const isLoading = useCreateAccountMutation.isPending;

  const renderStep = (): ReactNode => {
    switch (activeStep) {
      case 1:
        return (
          <CompanyInfoForm
            control={control}
            errors={errors}
            watch={watch}
            setValue={setValue}
          />
        );
      case 2:
        return <ReviewAndSubmit allData={formData} />;

      default:
        return <AdminInfoForm control={control} errors={errors} />;
    }
  };

  const {
    control,
    handleSubmit,
    trigger,
    formState: { errors },
    watch,
    setValue,
    getValues,
    reset
  } = useForm<RegistrationData>({
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    resolver: yupResolver(currentSchema as any),
    defaultValues: cloneDeep(formData),
    mode: "all"
  });

  const selectedBusinessType = watch("company.businessType");
  const orgLabels = getOrganizationLabelsForBusinessType(
    t,
    selectedBusinessType
  );

  const getStepTitle = (
    index: number,
    step: (typeof RegistrationStepConfig)[number]
  ) =>
    index === 1
      ? orgLabels.registrationStepTitle
      : t(step.titleTranslationKey, step.title);

  const stepDescription =
    activeStep === 1
      ? orgLabels.registrationStepDescription
      : t(currentStepConfig.labelTranslationKey, currentStepConfig.labelText);

  const handleNext = async () => {
    if (isCompanyStepBlockedByPlatformConfig) {
      return;
    }

    const valid = await trigger();

    // ⚠️ Client-side validation failed — not an API error, no Issue needed
    if (!valid) {
      sendAppLog(
        LOG_LEVEL.WARNING,
        "Registration step validation failed",
        buildLogContext(
          "auth",
          "registration",
          // userId/companyId/storeId not available — user is not yet registered
          undefined,
          undefined,
          undefined,
          { step: activeStep }
        )
      );
      return;
    }

    const currentValues = getValues();
    const updatedData = { ...formData, ...currentValues };
    const payload = cloneDeep(updatedData);

    dispatch(setFormData(payload));

    sendAppLog(
      LOG_LEVEL.INFO,
      "Registration advanced to next step",
      buildLogContext(
        "auth",
        "registration",
        // userId/companyId/storeId not available — user is not yet registered
        undefined,
        undefined,
        undefined,
        { fromStep: activeStep, toStep: activeStep + 1 }
      )
    );

    setActiveStep((p) => p + 1);
  };

  const handleBack = () => {
    sendAppLog(
      LOG_LEVEL.INFO,
      "Registration navigated back",
      buildLogContext(
        "auth",
        "registration",
        // userId/companyId/storeId not available — user is not yet registered
        undefined,
        undefined,
        undefined,
        { fromStep: activeStep, toStep: activeStep - 1 }
      )
    );

    setActiveStep((p) => p - 1);
  };

  const onSubmit = async (data: RegistrationData) => {
    const request = transformRegistrationToRequest(data);

    sendAppLog(
      LOG_LEVEL.INFO,
      "Started registration form submission",
      buildLogContext(
        "auth",
        "registration",
        // userId/companyId/storeId not available — account does not exist yet
        undefined,
        undefined,
        undefined
      )
    );

    try {
      await useCreateAccountMutation.mutateAsync(request, {
        onSuccess: (response) => {
          if (response.register.code !== API_RESPONSE_CODE.OK) return;

          // ✅ Success — navigate to login
          sendAppLog(
            LOG_LEVEL.INFO,
            "Registration completed successfully, navigating to login",
            buildLogContext(
              "auth",
              "registration",
              // userId/companyId/storeId not available — session is not yet created
              undefined,
              undefined,
              undefined
            )
          );

          navigate(localizedPath(PAGE_ROUTE_LOGIN, language, defaultLang));
        }
      });
    } catch (e) {
      // Error handling is delegated to useCreateAccount's onError —
      // this catch prevents an unhandled promise rejection in the UI layer
      console.error("something went wrong", e);
    }
  };

  useEffect(() => {
    const plain = cloneDeep(originalFormData ?? defaultRegistrationValues);
    const dataToReset: RegistrationData = {
      ...plain,
      admin: {
        ...plain.admin,
        phoneCountryCode:
          originalFormData?.admin?.phoneCountryCode || defaultPhoneCountryCode
      }
    };
    reset(dataToReset);
  }, [activeStep, originalFormData, defaultPhoneCountryCode]);

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: {
          xs: "column",
          sm: "column",
          md: "row"
        },
        width: "100%",
        minHeight: "100vh",
        height: "100vh"
      }}
    >
      <Box
        sx={{
          display: { xs: "none", sm: "none", md: "flex" },
          width: { md: "50%" },
          minHeight: { md: "100vh" },
          justifyContent: "center",
          alignItems: "center"
        }}
      >
        <AuthSidePanel />
      </Box>

      <AuthRightSidePanel>
        <Box
          component={motion.div}
          {...authFormCardMotion}
          sx={{
            flex: 1,
            width: "100%",
            display: "flex",
            flexDirection: "column",
            justifyContent: { xs: "flex-start", md: "center" },
            alignItems: "center",
            p: { xs: 2, sm: 3, md: 4 }
          }}
        >
          <Box
            sx={{
              width: "100%",
              maxWidth: { xs: "100%", sm: 640, md: 700 },
              display: "flex",
              flexDirection: "column"
            }}
          >
            <motion.div
              variants={authFormItemMotion}
              initial="initial"
              animate="animate"
            >
              <Typography
                variant="h5"
                fontWeight="bold"
                sx={{ mb: { xs: 2, sm: 3 }, textAlign: "center" }}
              >
                {t("register", "Register")}
              </Typography>
            </motion.div>

            <Stepper
              activeStep={activeStep}
              orientation="horizontal"
              sx={{
                width: "100%",
                maxWidth: { xs: "100%", sm: 640 },
                px: { xs: 0, sm: 2 },
                mb: { xs: 2, sm: 3 },
                "& .MuiStepLabel-root": { borderRadius: 2 },
                "& .MuiStepIcon-root": { borderRadius: 1 }
              }}
            >
              {RegistrationStepConfig.map((step, index) => (
                <Step key={step.titleTranslationKey}>
                  <StepLabel
                    icon={
                      <RenderRegistrationIcon
                        index={index}
                        activeStep={activeStep}
                      />
                    }
                  >
                    <Typography
                      variant="body2"
                      sx={{
                        display: {
                          xs: "none",
                          sm: "none",
                          md: "flex"
                        },
                        color: index <= activeStep ? BRAND_COLOR : "inherit"
                      }}
                    >
                      {getStepTitle(index, step)}
                    </Typography>
                  </StepLabel>
                </Step>
              ))}
            </Stepper>

            <Box
              sx={{
                mt: { xs: 2, sm: 3 },
                width: "100%",
                px: { xs: 0, sm: 2 },
                pb: { xs: 4, sm: 6 }
              }}
            >
              <form
                onSubmit={handleSubmit(onSubmit)}
                data-testid="registration-form"
              >
                <Typography
                  variant="body2"
                  color="text.secondary"
                  textAlign="center"
                  sx={{ mb: { xs: 2, sm: 3 } }}
                >
                  {stepDescription}
                </Typography>

                {renderStep()}

                {activeStep === RegistrationStepConfig.length - 1 && (
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    textAlign="center"
                    sx={{ mt: 2, mb: 0 }}
                  >
                    {t(
                      "by-clicking-agree-terms",
                      "By clicking, you agree to our "
                    )}
                    <Link
                      href={getPrivacyPolicyUrl(
                        language || defaultLang || "en"
                      )}
                      target="_blank"
                      rel="noopener noreferrer"
                      underline="always"
                      sx={linkButtonSx}
                      data-testid="registration-terms-link"
                    >
                      {t(
                        "terms-and-privacy-policy-link",
                        "Terms and Privacy Policy"
                      )}
                    </Link>
                  </Typography>
                )}

                {/*
                 * PageActionBar replaces the raw Box with manual
                 * flexDirection:{xs:"column",sm:"row"} — consistent with
                 * PasswordChangeForm and the rest of the system.
                 * align="start" keeps Back left, Next/Submit right.
                 * justify-content: space-between is handled via the spacer Box below.
                 */}
                <PageActionBar
                  align="end"
                  sx={{
                    justifyContent: "space-between",
                    mt: { xs: 4, sm: 5 }
                  }}
                >
                  {activeStep > 0 ? (
                    <AppButton
                      variant="secondary"
                      onClick={handleBack}
                      sx={{
                        minWidth: { xs: "100%", sm: 140 },
                        py: 1.5,
                        px: 3,
                        fontSize: "1rem"
                      }}
                      data-testid="registration-back"
                    >
                      {t("back", "Back")}
                    </AppButton>
                  ) : (
                    /* Spacer keeps Next/Submit right-aligned on desktop when Back is hidden */
                    <Box />
                  )}

                  {activeStep < RegistrationStepConfig.length - 1 ? (
                    <AppButton
                      variant="primary"
                      type="button"
                      disabled={isCompanyStepBlockedByPlatformConfig}
                      onClick={async (e) => {
                        e.preventDefault();
                        await handleNext();
                      }}
                      sx={{
                        minWidth: { xs: "100%", sm: 140 },
                        py: 1.5,
                        px: 3,
                        fontSize: "1rem"
                      }}
                      data-testid="registration-next"
                    >
                      {t("next", "Next")}
                    </AppButton>
                  ) : (
                    <AppButton
                      variant="primary"
                      type="submit"
                      disabled={isLoading}
                      sx={{
                        minWidth: { xs: "100%", sm: 140 },
                        py: 1.5,
                        px: 3,
                        fontSize: "1rem"
                      }}
                      data-testid="registration-submit"
                    >
                      {isLoading
                        ? t("processing", "processing...")
                        : t("submit", "Submit")}
                    </AppButton>
                  )}
                </PageActionBar>

                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "flex-end",
                    mt: { xs: 3, sm: 4 }
                  }}
                >
                  <Typography variant="body2" data-testid="login-signup-text">
                    {t("have-account-login", "Already have an account?")}{" "}
                    <Link
                      component="button"
                      type="button"
                      underline="hover"
                      sx={linkButtonSx}
                      onClick={() =>
                        navigate(
                          localizedPath(PAGE_ROUTE_LOGIN, language, defaultLang)
                        )
                      }
                      data-testid="registration-to-login"
                    >
                      {t("registration.login-link-text", "Login")}
                    </Link>
                  </Typography>
                </Box>
              </form>
            </Box>

            <Box
              sx={{
                position: {
                  xs: "relative",
                  sm: "relative",
                  md: "absolute"
                },
                bottom: { md: 16 },
                display: {
                  xs: "flex",
                  sm: "flex",
                  md: "none"
                },
                justifyContent: "center",
                width: "100%",
                mt: { xs: 4, sm: 6 }
              }}
            >
              <Typography
                variant="caption"
                textAlign="center"
                data-testid="login-left-footer"
              >
                {copyrightText("Kivunova")}
              </Typography>
            </Box>
          </Box>
        </Box>
      </AuthRightSidePanel>
    </Box>
  );
};

export default Registration;
